/**
 * The bookings list, one server page at a time with «عرض المزيد» (load more).
 * Every filter runs on the server, so `total` and `stats` describe the whole
 * filtered set, not the rows loaded so far (tasks/owner-screens-mobile.md D3).
 * Keeps the previous result on screen while a changed filter loads.
 *
 * Its own file, on secureApi only (tasks/owner-mobile-app-build.md §10 8c): the staff /bookings page and the
 * owner pages share it, and the owner app's bundle may hold no raw-axios code (services/bookingService.js has
 * some). hooks/useBookings.js re-exports it, so the staff page's import is unchanged.
 */
import { useInfiniteQuery, keepPreviousData } from '@tanstack/react-query';
import { secureApi } from '../services/secureApi';

const EMPTY_BOOKINGS = [];

/** One page: GET /api/v1/bookings with page/limit → { bookings, count, total, page, limit, hasMore, stats }. */
const getPage = async (params) => (await secureApi.get('/api/v1/bookings', { params })).data;

/**
 * @param {Object} filters - propertyId, ownerId, status, platform, search, checkInDayFrom/To (Riyadh days)
 * @param {number} pageSize
 * @returns {Object} { bookings, total, stats, fetchNextPage, hasNextPage, isFetchingNextPage, ... }
 */
export const useBookingsPaged = (filters = {}, pageSize = 20) => {
  const result = useInfiniteQuery({
    queryKey: ['bookings', 'paged', filters, pageSize],
    queryFn: ({ pageParam }) => getPage({ ...filters, page: pageParam, limit: pageSize }),
    initialPageParam: 1,
    // Read defensively: the front end can deploy before the backend that adds hasMore.
    getNextPageParam: (lastPage) => (lastPage?.hasMore ? lastPage.page + 1 : undefined),
    placeholderData: keepPreviousData,
    staleTime: 5 * 60 * 1000,
    refetchOnWindowFocus: true,
    retry: 1
  });

  const pages = result.data?.pages;
  return {
    ...result,
    bookings: pages ? pages.flatMap(p => p?.bookings ?? []) : EMPTY_BOOKINGS,
    total: pages?.[0]?.total ?? 0,
    stats: pages?.[0]?.stats ?? null
  };
};
